import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command';
import { User, Dumbbell, Swords, ShieldAlert, Target, Activity, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global Cmd/Ctrl+K athlete quick-search + jump palette.
 * Works from any page. Uses existing /api/athletes endpoint only.
 * Reduces clicks: Dashboard → Athletes list → scroll/search → open profile
 *            to: Cmd+K → type name → Enter.
 */
const GlobalQuickSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';

  // Keyboard shortcut: Cmd/Ctrl + K (staff only — the palette is a multi-athlete
  // tool; Player/Parent only see their own athlete, so the palette adds no value).
  useEffect(() => {
    if (!isStaff) return undefined;
    const handler = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isStaff]);

  // Lazy-load athletes on first open
  useEffect(() => {
    if (!open || loaded || !token) return;
    fetch(`${API}/athletes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        setAthletes(Array.isArray(data) ? data : []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded, token]);

  const filtered = useMemo(() => {
    if (!query.trim()) return athletes.slice(0, 20);
    const q = query.toLowerCase();
    return athletes
      .filter(
        (a) =>
          a.full_name?.toLowerCase().includes(q) ||
          a.squad?.toLowerCase().includes(q) ||
          a.position?.toLowerCase().includes(q) ||
          a.nationality?.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [athletes, query]);

  const go = useCallback(
    (path) => {
      setOpen(false);
      setQuery('');
      navigate(path);
    },
    [navigate],
  );

  // Quick nav targets (staff-relevant)
  const quickNav = useMemo(() => {
    if (!isStaff) return [];
    return [
      { label: 'Coach Today', path: '/dashboard', icon: Activity, testId: 'quick-nav-dashboard' },
      { label: 'Training Planner', path: '/training', icon: Dumbbell, testId: 'quick-nav-training' },
      { label: 'Match Reports', path: '/match-reports', icon: Swords, testId: 'quick-nav-matches' },
      { label: 'Injury & Recovery', path: '/injury-recovery', icon: ShieldAlert, testId: 'quick-nav-injury' },
      { label: 'PDP', path: '/pdp', icon: Target, testId: 'quick-nav-pdp' },
    ];
  }, [isStaff]);

  // Render nothing for Player/Parent — avoid confusing UI for a single-athlete view.
  // NOTE: must be AFTER all hooks to comply with rules-of-hooks.
  if (!isStaff) return null;

  return (
    <>
      {/* Discoverable trigger — full pill on desktop, icon-only on mobile for space. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400 hover:border-pfap-sage/60 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        data-testid="global-quick-search-trigger"
        aria-label="Open quick search"
      >
        <Search className="w-4 h-4" />
        <span>Search athletes…</span>
        <kbd className="ml-2 hidden lg:inline-flex items-center gap-0.5 rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-mono text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
          ⌘K
        </kbd>
      </button>
      {/* Mobile trigger — compact icon, thumb-friendly, floats top-right */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 text-gray-600 dark:text-gray-300 shadow-sm hover:shadow-md active:scale-95 transition-all"
        data-testid="global-quick-search-trigger-mobile"
        aria-label="Open quick search"
      >
        <Search className="w-4 h-4" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} data-testid="global-quick-search-dialog">
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Search athletes by name, squad, position…"
          data-testid="global-quick-search-input"
        />
        <CommandList data-testid="global-quick-search-list">
          <CommandEmpty>No matches. Try a different search.</CommandEmpty>

          {filtered.length > 0 && (
            <CommandGroup heading="Athletes">
              {filtered.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.full_name} ${a.squad || ''} ${a.position || ''}`}
                  onSelect={() => go(`/athlete/${a.id}/360`)}
                  data-testid={`global-quick-search-athlete-${a.id}`}
                >
                  <User className="w-4 h-4 mr-2 text-emerald-500" />
                  <span className="flex-1 truncate">{a.full_name}</span>
                  <span className="text-[10px] text-gray-400 ml-2">
                    {a.squad || '—'} · {a.position || '—'}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {quickNav.length > 0 && (
            <CommandGroup heading="Jump to">
              {quickNav.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.path}
                    value={item.label}
                    onSelect={() => go(item.path)}
                    data-testid={item.testId}
                  >
                    <Icon className="w-4 h-4 mr-2 text-gray-500" />
                    <span>{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalQuickSearch;
